import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    getStaff, approveStaff, rejectStaff, disableStaff, setStaffMobile,
    getBranches, setStaffBranches, getLimitSettings, updateLimitSettings, resetStaffLimit,
    StaffSummary, StaffStatus, Branch, LimitSettings,
} from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { useConfirm } from "../common/ConfirmDialog";
import { can } from "../../utils/roles";
import {
    FaCheck, FaBan, FaPowerOff, FaUserClock, FaUserCheck,
    FaUserSlash, FaSyncAlt, FaLock, FaPhone, FaCoins, FaUndo,
} from "react-icons/fa";

/**
 * Staff approval and the on/off switch. Super users and IT only.
 *
 * Everything here is also enforced server-side — the routes sit behind
 * requireSuperUser, so hiding a button is a courtesy, not a control.
 */

// ── Presentation ─────────────────────────────────────────────────────────────

const STATUS_STYLE: Record<string, { label: string; cls: string; icon: React.ReactNode }> = {
    pending:  { label: "Waiting",  cls: "bg-amber-100 text-amber-800",   icon: <FaUserClock /> },
    approved: { label: "Active",   cls: "bg-green-100 text-green-800",   icon: <FaUserCheck /> },
    rejected: { label: "Rejected", cls: "bg-red-100 text-red-700",       icon: <FaUserSlash /> },
    disabled: { label: "Disabled", cls: "bg-gray-200 text-gray-700",     icon: <FaPowerOff /> },
};

const FILTERS: { value: StaffStatus | "all"; label: string }[] = [
    { value: "all", label: "Everyone" },
    { value: "pending", label: "Waiting" },
    { value: "approved", label: "Active" },
    { value: "disabled", label: "Disabled" },
    { value: "rejected", label: "Rejected" },
];

const StatusPill: React.FC<{ status: string }> = ({ status }) => {
    const s = STATUS_STYLE[status] ?? { label: status, cls: "bg-gray-100 text-gray-700", icon: null };
    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${s.cls}`}>
            {s.icon}{s.label}
        </span>
    );
};

/**
 * The mobile number, editable in place.
 *
 * Staff cannot change their own — the OTP goes there, so a self-service edit
 * would let somebody redirect their own security code. This cell is the only
 * way it gets corrected, and the way a person locked out by a wrong number
 * gets back in.
 */
const MobileCell: React.FC<{
    staff: StaffSummary;
    editable: boolean;
    onSaved: (uid: string, mobile: string) => void;
}> = ({ staff, editable, onSaved }) => {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(staff.mobile || "");
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    const save = async () => {
        setSaving(true);
        setErr("");
        try {
            await setStaffMobile(staff.uid, value);
            onSaved(staff.uid, value);
            setEditing(false);
        } catch (e: any) {
            setErr(e?.response?.data?.errors?.[0]?.message
                ?? e?.response?.data?.error
                ?? "Could not save that number.");
        } finally {
            setSaving(false);
        }
    };

    if (!editing) {
        if (!editable) {
            return (
                <span className={staff.mobile ? "text-gray-600" : "text-amber-600"}>
                    {staff.mobile || "Not set"}
                </span>
            );
        }
        return (
            <button
                onClick={() => { setValue(staff.mobile || ""); setEditing(true); }}
                className="group inline-flex items-center gap-1.5 text-left"
                title="Only head office can change this"
            >
                <span className={staff.mobile ? "text-gray-600" : "text-amber-600"}>
                    {staff.mobile || "Not set"}
                </span>
                <FaPhone className="opacity-0 group-hover:opacity-60 text-xs" />
            </button>
        );
    }

    return (
        <div className="min-w-[170px]">
            <div className="flex gap-1.5">
                <input
                    autoFocus
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
                    placeholder="05X XXX XXXX"
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                />
                <button onClick={save} disabled={saving}
                    className="rounded bg-gray-900 px-2 py-1 text-xs text-white disabled:opacity-50">
                    {saving ? "…" : "Save"}
                </button>
                <button onClick={() => setEditing(false)}
                    className="rounded border border-gray-300 px-2 py-1 text-xs">✕</button>
            </div>
            {err && <div className="mt-1 text-xs text-red-600">{err}</div>}
        </div>
    );
};

/**
 * Which branches a staff member may work at.
 *
 * They pick one of these at each sign-in, and whichever they pick is stamped
 * onto their bookings. At least one is required — somebody with none can sign
 * in but cannot book anything, with nothing on screen explaining why.
 */
const BranchAssignmentCell: React.FC<{
    staff: StaffSummary;
    branches: Branch[];
    editable: boolean;
    onSaved: (uid: string, ids: string[]) => void;
}> = ({ staff, branches, editable, onSaved }) => {
    const [open, setOpen] = useState(false);
    const [chosen, setChosen] = useState<string[]>(staff.assignedBranchIds ?? []);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    const assigned = staff.assignedBranchIds ?? [];
    const nameOf = (id: string) => branches.find((b) => b.officeId === id)?.name ?? id;

    const toggle = (id: string) =>
        setChosen((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

    const save = async () => {
        setSaving(true);
        setErr("");
        try {
            const ids = await setStaffBranches(staff.uid, chosen);
            onSaved(staff.uid, ids);
            setOpen(false);
        } catch (e: any) {
            setErr(e?.response?.data?.errors?.[0]?.message
                ?? e?.response?.data?.error
                ?? "Could not save those branches.");
        } finally {
            setSaving(false);
        }
    };

    if (!open) {
        if (!editable) {
            return assigned.length === 0 ? (
                <span className="text-amber-600 text-xs">None assigned</span>
            ) : (
                <span className="text-gray-600 text-xs">
                    {assigned.map(nameOf).join(", ")}
                </span>
            );
        }
        return (
            <button
                onClick={() => { setChosen(assigned); setOpen(true); }}
                className="text-left"
                title="Change which branches this person can work at"
            >
                {assigned.length === 0 ? (
                    <span className="text-amber-600 text-xs">None assigned</span>
                ) : (
                    <span className="text-gray-600 text-xs">
                        {assigned.map(nameOf).join(", ")}
                    </span>
                )}
            </button>
        );
    }

    return (
        <div className="min-w-[190px] rounded-lg border border-gray-300 bg-white p-2">
            <div className="max-h-36 overflow-y-auto">
                {branches.map((b) => (
                    <label key={b.officeId} className="flex items-center gap-2 py-0.5 text-xs cursor-pointer">
                        <input
                            type="checkbox"
                            checked={chosen.includes(b.officeId)}
                            onChange={() => toggle(b.officeId)}
                        />
                        <span>{b.name}</span>
                    </label>
                ))}
            </div>
            {err && <div className="mt-1 text-xs text-red-600">{err}</div>}
            <div className="mt-2 flex gap-1.5">
                <button onClick={save} disabled={saving}
                    className="rounded bg-gray-900 px-2 py-1 text-xs text-white disabled:opacity-50">
                    {saving ? "…" : "Save"}
                </button>
                <button onClick={() => setOpen(false)}
                    className="rounded border border-gray-300 px-2 py-1 text-xs">Cancel</button>
            </div>
        </div>
    );
};

/**
 * The daily sales limit, which applies to every staff member alike.
 *
 * Super users and IT are not subject to it, so there is nothing per-person to
 * configure — one pair of numbers governs everybody.
 */
const LimitSettingsPanel: React.FC<{ editable: boolean }> = ({ editable }) => {
    const [settings, setSettings] = useState<LimitSettings | null>(null);
    const [limit, setLimit] = useState("");
    const [warn, setWarn] = useState("");
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        getLimitSettings().then((s) => {
            setSettings(s);
            setLimit(String(s.dailyLimit));
            setWarn(String(s.warnAt));
        }).catch(() => setErr("Could not load the limit."));
    }, []);

    const save = async () => {
        setSaving(true);
        setErr("");
        try {
            const next = await updateLimitSettings(Number(limit), Number(warn));
            setSettings(next);
            setEditing(false);
        } catch (e: any) {
            setErr(e?.response?.data?.errors?.[0]?.message
                ?? e?.response?.data?.error
                ?? "That limit could not be saved.");
        } finally {
            setSaving(false);
        }
    };

    if (!settings) return null;
    const money = (n: number) => `${settings.currency} ${n.toLocaleString()}`;

    return (
        <div className="mb-5 rounded-xl border border-gray-200 bg-white px-4 py-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                    <FaCoins className="text-gray-400" />
                    {editing ? (
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                            <label className="text-gray-500">Daily limit</label>
                            <input value={limit} onChange={(e) => setLimit(e.target.value.replace(/[^\d.]/g, ""))}
                                className="w-28 rounded border border-gray-300 px-2 py-1" />
                            <label className="text-gray-500">Warn at</label>
                            <input value={warn} onChange={(e) => setWarn(e.target.value.replace(/[^\d.]/g, ""))}
                                className="w-28 rounded border border-gray-300 px-2 py-1" />
                        </div>
                    ) : (
                        <span className="text-sm text-gray-700">
                            Every staff member can sell <strong>{money(settings.dailyLimit)}</strong> a day,
                            with a warning at <strong>{money(settings.warnAt)}</strong>.
                        </span>
                    )}
                </div>

                <div className="flex gap-2">
                    {editing ? (
                        <>
                            <button onClick={save} disabled={saving}
                                className="rounded-lg bg-gray-900 px-3 py-1.5 text-xs text-white disabled:opacity-50">
                                {saving ? "Saving…" : "Save"}
                            </button>
                            <button onClick={() => setEditing(false)}
                                className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs">Cancel</button>
                        </>
                    ) : editable ? (
                        <button onClick={() => setEditing(true)}
                            className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">
                            Change
                        </button>
                    ) : null}
                </div>
            </div>
            {err && <div className="mt-2 text-xs text-red-600">{err}</div>}
            <p className="mt-2 text-xs text-gray-500">
                Resets at midnight Dubai time. Super users and IT are not limited.
            </p>
        </div>
    );
};

const fmtDate = (iso?: string | null) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

// ── Screen ───────────────────────────────────────────────────────────────────

const StaffManager: React.FC = () => {
    const confirm = useConfirm();
    const { profile } = useAuth();
    const [staff, setStaff] = useState<StaffSummary[]>([]);
    const [branches, setBranches] = useState<Branch[]>([]);
    const [filter, setFilter] = useState<StaffStatus | "all">("all");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    /** uid currently being acted on, so only that row's buttons spin. */
    const [busy, setBusy] = useState<string | null>(null);

    const allowed = can(profile, "staff");
    const editable = can(profile, "staff", "manage");
    const canLimits = can(profile, "limits", "manage");

    const load = useCallback(async () => {
        if (!allowed) return;
        setLoading(true);
        setError("");
        try {
            setStaff(await getStaff(filter));
        } catch (err: any) {
            setError(err?.response?.data?.errors?.[0]?.message
                ?? err?.response?.data?.error
                ?? "Could not load the staff list.");
        } finally {
            setLoading(false);
        }
    }, [filter, allowed]);

    useEffect(() => { load(); }, [load]);

    // Branches change rarely, so fetch them once rather than on every filter.
    // A failure here is not fatal — the branch cell falls back to showing ids.
    useEffect(() => {
        if (!allowed) return;
        getBranches().then(setBranches).catch(() => setBranches([]));
    }, [allowed]);

    /**
     * Clear somebody's used amount for today.
     *
     * Recorded rather than destructive — the bookings stay exactly as they
     * were, so the sales figures are unaffected. Only the limit starts again.
     */
    const resetLimit = async (uid: string, name: string) => {
        const ok = await confirm({
            title: "Clear today's used amount?",
            message: `${name} will be able to sell up to the full limit again today. ` +
                `Their sales figures are not affected.`,
            confirmLabel: "Clear it",
        });
        if (!ok) return;
        setBusy(uid);
        setError("");
        try {
            const r = await resetStaffLimit(uid);
            window.alert(`${name} was on ${r.usedBefore.toLocaleString()} and is now on ${r.usedAfter.toLocaleString()}.`);
        } catch (e: any) {
            setError(e?.response?.data?.errors?.[0]?.message
                ?? e?.response?.data?.error
                ?? "That reset could not be applied.");
        } finally {
            setBusy(null);
        }
    };

    const act = async (uid: string, action: "approve" | "reject" | "disable", name: string) => {
        // Disabling throws someone out of their session mid-work, and rejecting
        // is visible to them at their next sign-in. Both are worth a moment's
        // pause; approving is not.
        if (action !== "approve") {
            const ok = await confirm({
                title: action === "disable" ? `Switch off ${name}?` : `Reject ${name}?`,
                message: action === "disable"
                    ? `${name} will be signed out immediately and cannot sell until switched back on.`
                    : `${name} will be told at their next sign-in. This can be undone later.`,
                confirmLabel: action === "disable" ? "Switch off" : "Reject",
                tone: "danger",
            });
            if (!ok) return;
        }

        setBusy(uid);
        setError("");
        try {
            const fn = action === "approve" ? approveStaff : action === "reject" ? rejectStaff : disableStaff;
            const updated = await fn(uid);
            // Patch in place rather than refetching, so the list does not jump
            // under the cursor while someone works through the queue.
            setStaff((prev) => prev.map((s) => (s.uid === uid ? { ...s, ...updated } : s)));
        } catch (err: any) {
            setError(err?.response?.data?.errors?.[0]?.message
                ?? err?.response?.data?.error
                ?? "That change could not be saved.");
        } finally {
            setBusy(null);
        }
    };

    const pendingCount = useMemo(
        () => staff.filter((s) => s.staffStatus === "pending").length,
        [staff],
    );

    if (!allowed) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center px-4">
                <div className="max-w-sm text-center">
                    <FaLock className="mx-auto text-gray-400 mb-3" size={30} />
                    <h1 className="text-lg font-semibold text-gray-900 mb-1">Not available</h1>
                    <p className="text-sm text-gray-500">Only head office can manage staff accounts.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto px-4 py-8">
            <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-900">Staff accounts</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Approve new staff, and switch existing accounts on or off.
                        {pendingCount > 0 && (
                            <span className="ml-1 font-medium text-amber-700">
                                {pendingCount} waiting.
                            </span>
                        )}
                    </p>
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    className="flex items-center gap-2 text-sm border border-gray-300 rounded-lg px-3 py-2 hover:bg-gray-50 disabled:opacity-50"
                >
                    <FaSyncAlt className={loading ? "animate-spin" : ""} /> Refresh
                </button>
            </div>

            <LimitSettingsPanel editable={canLimits} />

            <div className="flex flex-wrap gap-2 mb-4">
                {FILTERS.map((f) => (
                    <button
                        key={f.value}
                        onClick={() => setFilter(f.value)}
                        className={`px-3 py-1.5 rounded-full text-sm border transition ${
                            filter === f.value
                                ? "bg-gray-900 text-white border-gray-900"
                                : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                        }`}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            {error && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {error}
                </div>
            )}

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
                            <tr>
                                <th className="px-4 py-3 font-semibold">Name</th>
                                <th className="px-4 py-3 font-semibold">Email</th>
                                <th className="px-4 py-3 font-semibold">Phone</th>
                                <th className="px-4 py-3 font-semibold">Branches</th>
                                <th className="px-4 py-3 font-semibold">Status</th>
                                <th className="px-4 py-3 font-semibold">Signed up</th>
                                <th className="px-4 py-3 font-semibold text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading && (
                                <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">Loading…</td></tr>
                            )}

                            {!loading && staff.length === 0 && (
                                <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">
                                    Nobody here yet.
                                </td></tr>
                            )}

                            {!loading && staff.map((s) => {
                                const isBusy = busy === s.uid;
                                const canApprove = editable && (s.staffStatus === "pending" || s.staffStatus === "rejected" || s.staffStatus === "disabled");
                                const canReject  = editable && s.staffStatus === "pending";
                                const canDisable = editable && s.staffStatus === "approved";
                                const canReset   = canLimits && s.staffStatus === "approved";
                                const name = s.displayName || s.email;

                                return (
                                    <tr key={s.uid} className="hover:bg-gray-50">
                                        <td className="px-4 py-3">
                                            <div className="font-medium text-gray-900">{s.displayName || "—"}</div>
                                            {!s.emailVerified && (
                                                <div className="text-xs text-amber-600 mt-0.5">Email not verified</div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-gray-600">{s.email}</td>
                                        <td className="px-4 py-3">
                                            <MobileCell
                                                staff={s}
                                                editable={editable}
                                                onSaved={(uid, mobile) =>
                                                    setStaff((prev) => prev.map((r) => (r.uid === uid ? { ...r, mobile } : r)))
                                                }
                                            />
                                        </td>
                                        <td className="px-4 py-3">
                                            <BranchAssignmentCell
                                                staff={s}
                                                branches={branches}
                                                editable={editable}
                                                onSaved={(uid, ids) =>
                                                    setStaff((prev) => prev.map((r) => (r.uid === uid ? { ...r, assignedBranchIds: ids } : r)))
                                                }
                                            />
                                        </td>
                                        <td className="px-4 py-3"><StatusPill status={s.staffStatus} /></td>
                                        <td className="px-4 py-3 text-gray-500">{fmtDate(s.createdAt)}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex justify-end gap-2">
                                                {canApprove && (
                                                    <button
                                                        onClick={() => act(s.uid, "approve", name)}
                                                        disabled={isBusy}
                                                        className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
                                                    >
                                                        <FaCheck />
                                                        {s.staffStatus === "pending" ? "Approve" : "Enable"}
                                                    </button>
                                                )}
                                                {canReject && (
                                                    <button
                                                        onClick={() => act(s.uid, "reject", name)}
                                                        disabled={isBusy}
                                                        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                                                    >
                                                        <FaBan /> Reject
                                                    </button>
                                                )}
                                                {canDisable && (
                                                    <button
                                                        onClick={() => act(s.uid, "disable", name)}
                                                        disabled={isBusy}
                                                        className="inline-flex items-center gap-1.5 rounded-lg border border-red-300 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                                                    >
                                                        <FaPowerOff /> Disable
                                                    </button>
                                                )}
                                                {canReset && (
                                                    <button
                                                        onClick={() => resetLimit(s.uid, name)}
                                                        disabled={isBusy}
                                                        title="Clear today's used amount"
                                                        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                                    >
                                                        <FaUndo /> Reset limit
                                                    </button>
                                                )}
                                                {!canApprove && !canReject && !canDisable && !canReset && (
                                                    <span className="text-xs text-gray-400">—</span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <p className="mt-4 text-xs text-gray-500">
                A rejected account is not gone — you can still approve it later. Switching someone off
                signs them out straight away. Click a phone number to change it: security codes are sent
                there, so staff cannot edit their own. Staff choose one of their assigned branches each
                time they sign in, and that branch is recorded against their bookings.
            </p>
        </div>
    );
};

export default StaffManager;
